import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { DutyStatus, crewRoleLabels } from '@@api/shared'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Get background color classes for a given duty status.
 */
export function getDutyStatusColor(status: DutyStatus): string {
  const colors: Record<DutyStatus, string> = {
    [DutyStatus.OnDuty]: 'bg-status-on-duty',
    [DutyStatus.OffDuty]: 'bg-status-off-duty',
    [DutyStatus.Training]: 'bg-status-training',
    [DutyStatus.Standby]: 'bg-status-standby',
    [DutyStatus.Leave]: 'bg-status-leave',
    [DutyStatus.Sick]: 'bg-status-sick',
    [DutyStatus.Positioning]: 'bg-status-positioning',
  }
  return colors[status] ?? 'bg-status-off-duty'
}

/**
 * Get ring color classes for a given duty status.
 */
export function getDutyStatusRingColor(status: DutyStatus): string {
  const colors: Record<DutyStatus, string> = {
    [DutyStatus.OnDuty]: 'ring-roster-on-duty',
    [DutyStatus.OffDuty]: 'ring-roster-off-duty',
    [DutyStatus.Training]: 'ring-roster-training',
    [DutyStatus.Standby]: 'ring-roster-standby',
    [DutyStatus.Leave]: 'ring-status-leave',
    [DutyStatus.Sick]: 'ring-status-sick',
    [DutyStatus.Positioning]: 'ring-status-positioning',
  }
  return colors[status] ?? 'ring-muted'
}

/**
 * Format a crew role enum value for display, e.g. "LeadFlightAttendant" → "Lead Flight Attendant".
 */
export function formatCrewRole(role?: string | null): string {
  if (!role) return ''
  return crewRoleLabels[role as keyof typeof crewRoleLabels] ?? role
}

/**
 * Get initials from first and last name.
 * Handles optional/empty names gracefully.
 */
export function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.charAt(0)?.toUpperCase() || ''
  const last = lastName?.charAt(0)?.toUpperCase() || ''
  return first + last || '??'
}

/**
 * Format a duration between two dates as a human-readable string.
 * Examples: "60 mins", "120 mins", "3 hours"
 */
export function formatDuration(departure: string | Date, arrival: string | Date): string {
  const dep = new Date(departure).getTime()
  const arr = new Date(arrival).getTime()
  const totalMinutes = Math.round((arr - dep) / 60000)
  if (totalMinutes < 120) return `${totalMinutes} mins`
  const hours = Math.round(totalMinutes / 60)
  return `${hours} hours`
}

/**
 * Get a human-readable label for a repeat pattern.
 */
export function getPatternLabel(daysOn: number | null | undefined, daysOff: number | null | undefined): string {
  if (daysOn == null || daysOff == null) return 'None'

  if (daysOn === daysOff) {
    return `${daysOn} Days on/off`
  }

  return `${daysOn} Days on / ${daysOff} Days off`
}
